import { Expose, Type } from "class-transformer";
import { IsArray, IsBoolean, IsEnum, IsNumber, IsOptional, IsString, ValidateNested } from "class-validator";
import { InputAttributeItem } from "@/models/components/inputs/InputAttributeItem";
import { VALUE_TYPES } from "@/static/valueTypes";
import { TRADE_TERM_APPLY_TO } from "@/static/tradeTermApplyToTypes";
import { FORM_OF_PAYMENTS } from "@/static/formOfPayments";

export class AttributeItem extends InputAttributeItem {
  @IsString()
  @Expose()
  tmpId!: string;
  @IsEnum(VALUE_TYPES)
  @Expose()
  // eslint-disable-next-line
  // @ts-ignore
  dataType!: VALUE_TYPES;
  @IsBoolean()
  @Expose()
  isPrintable!: boolean;
  @IsOptional()
  @IsString()
  @Expose()
  label!: string | null;
  @IsOptional()
  @IsString()
  @Expose()
  value!: string | null;
  @IsBoolean()
  @Expose()
  // eslint-disable-next-line
  // @ts-ignore
  isRequired!: boolean;
  @IsNumber()
  @Expose()
  ordinal!: number;
  @IsOptional()
  @IsNumber()
  @Expose()
  attributePrototypeId!: number | null;
  @IsOptional()
  @IsArray()
  @Expose()
  // eslint-disable-next-line
  // @ts-ignore
  enumItems!: null | Array<string>;

  @IsBoolean()
  @Expose()
  isVisible!: boolean;

  @Expose()
  isHidden!: boolean;

  public static toInputAttributeItem(i: AttributeItem): InputAttributeItem {
    const inputAttributeItem = new InputAttributeItem();
    inputAttributeItem.isRequired = i.isRequired ?? false;
    inputAttributeItem.dataType = i.dataType as VALUE_TYPES;
    inputAttributeItem.enumItems = i.enumItems;
    return inputAttributeItem;
  }
}

export class TradeTermItem {
  @IsString()
  @Expose()
  tmpId!: string;
  @IsEnum(TRADE_TERM_APPLY_TO)
  @Expose()
  appliesTo!: TRADE_TERM_APPLY_TO;
  @IsNumber()
  @Expose()
  amount!: number;
  @IsString()
  @Expose()
  name!: string;
  @IsOptional()
  @IsString()
  @Expose()
  serviceTypeCode!: string | null;
  @IsBoolean()
  @Expose()
  isHidden!: boolean;
  @IsBoolean()
  @Expose()
  separateItem!: boolean;
}

export class BookingGetTemporaryItem {
  @IsOptional()
  @IsString()
  @Expose()
  tmpId: string | null = null;
  @IsNumber()
  @Expose()
  fareBase = 0;
  @IsOptional()
  @IsString()
  @Expose()
  firstNumber: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  clientName: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  clientSurname: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  serviceTypeGroupCode: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  serviceTypeCode: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  comment: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  costDocumentNumber: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  costDocumentKsefNumber: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  agentId: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  saleDate: string | null = null;
  @IsBoolean()
  @Expose()
  hasCostDocumentFile = false;
  @IsNumber()
  @Expose()
  status = 0;
  @IsBoolean()
  @Expose()
  canEdit = false;
  @IsBoolean()
  @Expose()
  isFromBSP = false;
  @IsOptional()
  @IsEnum(FORM_OF_PAYMENTS)
  @Expose()
  formOfPaymentId: FORM_OF_PAYMENTS | null = FORM_OF_PAYMENTS.Transfer;
  @IsOptional()
  @IsNumber()
  @Expose()
  creditCardId: number | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  creditCardNumber = "";

  @IsNumber()
  @Expose()
  grandTotalInCurrency = 0;
  @IsNumber()
  @Expose()
  grandTotal = 0;
  @IsString()
  @Expose()
  currency = "PLN";
  @IsNumber()
  @Expose()
  quantity = 1;
  @IsOptional()
  @IsString()
  @Expose()
  exchangeDate: string | null = null;
  @IsOptional()
  @IsNumber()
  @Expose()
  exchangeRate: number | null = null;

  @IsNumber()
  @Expose()
  netValueInCurrency = 0;
  @IsNumber()
  @Expose()
  netValue = 0;
  @IsNumber()
  @Expose()
  netPriceInCurrency = 0;
  @IsNumber()
  @Expose()
  netPrice = 0;
  @IsNumber()
  @Expose()
  fareBaseNet = 0;
  @IsString()
  @Expose()
  vat = "0";

  @IsOptional()
  @IsNumber()
  @Expose()
  purchaseNetPrice: number | null = null;
  @IsOptional()
  @IsNumber()
  @Expose()
  purchaseNetPriceInCurrency: number | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  purchaseNetPriceCurrency: string | null = null;
  @IsOptional()
  @IsString()
  @Expose()
  purchaseNetPriceExchangeDate: string | null = null;
  @IsOptional()
  @IsNumber()
  @Expose()
  purchaseNetPriceExchangeRate: number | null = null;

  @IsOptional()
  @IsString()
  @Expose()
  originalBookingItemTmpId: string | null = null;

  @IsBoolean()
  @Expose()
  isBlockedToInvoiced = false;

  // @Type(() => BookingAttribute)
  // @ValidateNested()
  // @Expose()
  // attribute!: Array<BookingAttribute>;
}

export class BookingItemIdItem {
  @IsString()
  @Expose()
  id!: string;
  @IsNumber()
  @Expose()
  ordinal!: number;
  @IsString()
  @IsOptional()
  @Expose()
  recordLocator!: string | null;
  @IsString()
  @IsOptional()
  @Expose()
  name!: string | null;
  @IsString()
  @IsOptional()
  @Expose()
  surname!: string | null;
  @IsBoolean()
  @Expose()
  createVoucher!: boolean;
}

export class BookingGetTemporaryItemResponse {
  @Type(() => BookingGetTemporaryItem)
  @ValidateNested()
  @Expose()
  bookingItemTmp!: BookingGetTemporaryItem;

  @Type(() => BookingItemIdItem)
  @ValidateNested()
  @Expose()
  bookingItemIds!: Array<BookingItemIdItem>;
}
